import React, {useEffect, useRef, useState} from "react";
import Alert from "@amzn/meridian/alert";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import Loader from "@amzn/meridian/loader";
import Modal from "@amzn/meridian/modal";
import closeTokens from "@amzn/meridian-tokens/base/icon/close-large";
import {colorGray700} from "@amzn/meridian-tokens/base/color";
import {FormattedDate, FormattedMessage, injectIntl} from "react-intl";
import {buildTitle, formatWeightUnit} from "../../util/CommonUtils";
import {searchEWasteItems} from "../../commons/api/SearchEWasteItems";
import {WTN_MSG} from "../../commons/constants/WtnMessageIds";
import {FILTER_TYPE, QUERY_TYPE, QUERY_FIELD} from "../../commons/constants/WadmQueryFilter";

const PAGE_SIZE = 100;

/**
 * Build the queryFilters array used by SearchEWasteItems to scope to a
 * single container.
 */
function buildContainerIdFilter(containerId) {
    return [{
        filterType: FILTER_TYPE.MUST,
        queryParams: [{
            field: QUERY_FIELD.CONTAINER_ID,
            queryType: QUERY_TYPE.MATCH,
            value: [containerId]
        }]
    }];
}

const ContainerDetailsModal = ({container, open, onClose, intl}) => {
    const [items, setItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [hasLoadMoreError, setHasLoadMoreError] = useState(false);
    const sentinelRef = useRef(null);
    // Stable primitive id derived from the container prop. Effects key on this
    // rather than the whole `container` object so they re-run on an actual
    // container change, not on unrelated parent re-renders that hand down a new
    // object identity. (Optional chaining is intentionally avoided — the
    // project's parser does not support it.)
    const containerId = container && container.id;
    // Always-current mirror of containerId. The load-more observer callback is
    // created inside an effect and closes over that render's containerId; to
    // detect that the container has since changed we must compare the id captured
    // at subscribe time against the LATEST id, which this ref provides.
    const containerIdRef = useRef(containerId);
    containerIdRef.current = containerId;

    useEffect(() => {
        if (!open || !container) {
            return undefined;
        }

        let ignore = false;
        // Reset any state carried over from a previously-viewed container so a
        // reopen starts clean. Without this, stale items/hasLoadMoreError/
        // loadingMore from the prior container survive until the async fetch
        // resolves, and the load-more observer can fire against stale state and
        // render a bogus error Alert that only clears on a full page refresh.
        setLoading(true);
        setErrorMsg("");
        setItems([]);
        setTotalCount(0);
        setHasLoadMoreError(false);
        setLoadingMore(false);

        const queryFilters = buildContainerIdFilter(container.id);

        searchEWasteItems({queryFilters}).then((result) => {
            if (ignore) return;
            if (result.success) {
                setItems(result.entities || []);
                setTotalCount(result.count || 0);
            } else {
                console.error("searchEWasteItems failed", {containerId: container.id, result});
                setErrorMsg(intl.formatMessage({
                    id: WTN_MSG.CONTAINER.DETAILS.LOAD_FAILED,
                    defaultMessage: "Failed to load container items."
                }));
            }
            setLoading(false);
        });

        return () => { ignore = true; };
        // Keyed on the stable container id (not the whole container object) so
        // this effect re-runs on an actual container change, not on unrelated
        // parent re-renders that produce a new object identity.
    }, [open, containerId, intl]);

    useEffect(() => {
        if (!open) {
            setItems([]);
            setTotalCount(0);
            setLoading(false);
            setLoadingMore(false);
            setErrorMsg("");
            setHasLoadMoreError(false);
        }
    }, [open]);

    useEffect(() => {
        if (loading || loadingMore || hasLoadMoreError) return undefined;
        if (items.length >= totalCount) return undefined;
        const node = sentinelRef.current;
        if (!node) return undefined;

        let ignore = false;
        // Capture the container id at subscribe time. On reopen the effect can
        // re-run with a different container; if a queued observer callback then
        // fires for the previous container it must bail rather than issue a
        // load-more with a stale `from`.
        const subscribedId = containerId;
        const observer = new IntersectionObserver((entries) => {
            if (subscribedId !== containerIdRef.current) return;
            if (entries[0] && entries[0].isIntersecting) {
                observer.disconnect();
                const from = items.length;
                setLoadingMore(true);
                setErrorMsg("");
                const queryFilters = buildContainerIdFilter(subscribedId);
                searchEWasteItems({from, size: PAGE_SIZE, queryFilters}).then((result) => {
                    if (ignore) return;
                    if (result.success) {
                        const newEntities = result.entities || [];
                        if (newEntities.length === 0) {
                            setTotalCount(from);
                        } else {
                            setItems((prev) => [...prev, ...newEntities]);
                        }
                    } else {
                        console.error("searchEWasteItems failed", {containerId: subscribedId, from, result});
                        setHasLoadMoreError(true);
                        setErrorMsg(intl.formatMessage({
                            id: WTN_MSG.CONTAINER.DETAILS.LOAD_FAILED,
                            defaultMessage: "Failed to load container items."
                        }));
                    }
                    setLoadingMore(false);
                });
            }
        }, {threshold: 0.1});

        observer.observe(node);
        return () => {
            ignore = true;
            observer.disconnect();
        };
        // Keyed on the stable container id rather than the whole container
        // object, so a new object identity from the parent does not re-subscribe
        // the observer against stale items.length.
    }, [items.length, totalCount, loadingMore, loading, hasLoadMoreError, containerId, intl]);

    if (!open || !container) return null;

    return (
        <Modal
            open={open}
            onClose={onClose}
            describe="Container details"
            width="70vw"
            scrollContainer="modal"
        >
            {/* Header */}
            <Row
                alignmentHorizontal="justify"
                alignmentVertical="center"
                spacingInset="medium large"
                backgroundColor={colorGray700}
            >
                <Text type="h200" color="inverted">{buildTitle(container)}</Text>
                <Button type="icon" onClick={onClose}>
                    <Icon tokens={closeTokens}>
                        <FormattedMessage id={WTN_MSG.CONTAINER.DETAILS.CLOSE} defaultMessage="Close" />
                    </Icon>
                </Button>
            </Row>

            <Column spacingInset="large" spacing="large">
                {/* Container information */}
                <Column spacing="small">
                    <Text type="h100">
                        <FormattedMessage id={WTN_MSG.CONTAINER.DETAILS.CONTAINER_INFO} defaultMessage="CONTAINER INFORMATION" />
                    </Text>
                    <Row widths={["1/3", "1/3", "1/3"]} spacing="medium">
                        <Column spacing="xsmall">
                            <Text type="b100" color="secondary">
                                <FormattedMessage id={WTN_MSG.CONTAINER.DETAILS.CONTAINER_ID} defaultMessage="CONTAINER ID" />
                            </Text>
                            <Text type="b200">{container.containerDisplayId || "—"}</Text>
                        </Column>
                        <Column spacing="xsmall">
                            <Text type="b100" color="secondary">
                                <FormattedMessage id={WTN_MSG.CONTAINER.DETAILS.STATUS} defaultMessage="STATUS" />
                            </Text>
                            <Text type="b200">{container.status || "—"}</Text>
                        </Column>
                        <Column spacing="xsmall">
                            <Text type="b100" color="secondary">
                                <FormattedMessage id={WTN_MSG.CONTAINER.DETAILS.CREATED_DATE} defaultMessage="CONTAINER CREATION DATE" />
                            </Text>
                            <Text type="b200">
                                {container.createdAt
                                    ? <FormattedDate value={container.createdAt} year="numeric" month="2-digit" day="2-digit" />
                                    : "—"}
                            </Text>
                        </Column>
                    </Row>
                </Column>

                {/* Weight progress */}
                {container.maxWeight && container.usage && container.usage.weight && container.maxWeight.weight > 0 && (
                    <Column spacing="xsmall">
                        <Text type="b100" color="secondary">
                            <FormattedMessage id={WTN_MSG.CONTAINER.DETAILS.WEIGHT_PROGRESS} defaultMessage="WEIGHT PROGRESS" />
                        </Text>
                        <Text type="b200">
                            {container.usage.weight.weight} / {container.maxWeight.weight} {formatWeightUnit(container.maxWeight.unit)} ({Math.round((container.usage.weight.weight / container.maxWeight.weight) * 100)}%)
                        </Text>
                    </Column>
                )}

                {/* Items section */}
                <Column spacing="small">
                    <Text type="h100">
                        <FormattedMessage
                            id={WTN_MSG.CONTAINER.DETAILS.ITEMS}
                            defaultMessage="CONTAINER ITEMS ({count})"
                            values={{count: totalCount}}
                        />
                    </Text>

                    {loading ? (
                        <Row alignmentHorizontal="center" spacingInset="large">
                            <Loader />
                        </Row>
                    ) : items.length === 0 ? (
                        <React.Fragment>
                            {errorMsg && <Alert type="error">{errorMsg}</Alert>}
                            {!errorMsg && (
                                <Text type="b200" color="secondary">
                                    <FormattedMessage id={WTN_MSG.CONTAINER.DETAILS.NO_ITEMS} defaultMessage="No items in this container" />
                                </Text>
                            )}
                        </React.Fragment>
                    ) : (
                        <Column spacing="small">
                            {items.map((item) => (
                                <Row key={item.id} alignmentHorizontal="justify" alignmentVertical="center">
                                    <Column spacing="none">
                                        <Text type="b200">{item.itemName}</Text>
                                        <Text type="b200" color="secondary">
                                            <FormattedMessage
                                                id={WTN_MSG.CONTAINER.DETAILS.WASTE_CODE_ADDED}
                                                defaultMessage="Waste Code: {code} | Added: {date}"
                                                values={{
                                                    code: item.ewcCode || item.wasteProfileId || "—",
                                                    date: item.createdAt
                                                        ? <FormattedDate value={item.createdAt} year="numeric" month="2-digit" day="2-digit" />
                                                        : "—"
                                                }}
                                            />
                                        </Text>
                                    </Column>
                                    {item.totalWeight && (
                                        <Text type="b200">
                                            {item.totalWeight.weight} {formatWeightUnit(item.totalWeight.unit)}
                                        </Text>
                                    )}
                                </Row>
                            ))}
                            {items.length < totalCount && (
                                <Row alignmentHorizontal="start" alignmentVertical="center" spacing="small">
                                    <Text type="b100" color="secondary">
                                        <FormattedMessage
                                            id={WTN_MSG.CONTAINER.DETAILS.SHOWING_COUNT}
                                            defaultMessage="Showing {shown} of {total}"
                                            values={{shown: items.length, total: totalCount}}
                                        />
                                    </Text>
                                    {loadingMore && <Loader size="small" />}
                                    <div ref={sentinelRef} style={{height: "1px"}} />
                                </Row>
                            )}
                            {errorMsg && <Alert type="error">{errorMsg}</Alert>}
                        </Column>
                    )}
                </Column>
            </Column>
        </Modal>
    );
};

export default injectIntl(ContainerDetailsModal);



// WEBPACK FOOTER //
// ./src/components/wtn/ContainerDetailsModal.js